import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Phone, Sparkles } from 'lucide-react';
import Loader from './Loader';
import { useSiteData } from '@/context/SiteDataContext';
import { optimizeImageUrl } from '@/lib/imageUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const HeroSection = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [heroImages, setHeroImages] = useState([]);
  const { siteSettings } = useSiteData();

  useEffect(() => {
    // Fetch hero images
    fetch(`${API_URL}/api/hero-images`)
      .then(res => res.json())
      .then(data => setHeroImages(data))
      .catch(err => console.error('Error fetching hero images:', err));
  }, []);

  useEffect(() => {
    if (heroImages.length === 0) return;
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroImages.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [heroImages.length]);

  const nextSlide = () => {
    if (heroImages.length === 0) return;
    setCurrentSlide((prev) => (prev + 1) % heroImages.length);
  };

  const prevSlide = () => {
    if (heroImages.length === 0) return;
    setCurrentSlide((prev) => (prev - 1 + heroImages.length) % heroImages.length);
  };

  const handleConsultation = () => {
    const message = encodeURIComponent("Hi! I'd like to get a free travel consultation for my international family trip.");
    window.open(`https://wa.me/${siteSettings.whatsappNumber.replace('+', '')}?text=${message}`, '_blank');
  };

  if (heroImages.length === 0) {
    return (
      <section className="hero-section">
        <Loader />
      </section>
    );
  }

  return (
    <section className="hero-section">
      <div className="hero-carousel-wrapper">
        <div className="carousel-wrapper">
          <AnimatePresence>
            {heroImages.map((image, index) => (
              index === currentSlide && (
                <motion.div
                  key={image.id}
                  initial={{ opacity: 0, scale: 1.06 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 1.1, ease: 'easeOut' }}
                  className="carousel-slide active"
                  style={{ backgroundImage: `url(${optimizeImageUrl(image.url, 1920, 80)})` }}
                >
                  <div className="carousel-overlay"></div>
                </motion.div>
              )
            ))}
          </AnimatePresence>
        </div>

        <div className="hero-overlay-content">
          <div className="hero-text-card">
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: 'easeOut' }}
              className="beautiful-badge hero-badge"
              style={{ marginBottom: '1.25rem' }}
            >
              <Sparkles size={14} />
              <span>Trusted by Families</span>
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
              className="hero-overlay-title display-heading"
            >
              International Family Holidays,<br />
              <span className="gradient-text-rich">Made Easy</span>
            </motion.h1>
            <motion.button
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
              whileHover={{ y: -4, boxShadow: '0 18px 45px rgba(37, 211, 102, 0.5)' }}
              whileTap={{ scale: 0.97 }}
              className="hero-consult-btn"
              onClick={handleConsultation}
            >
              <Phone size={20} />
              Get Free Travel Consultation
            </motion.button>
          </div>
        </div>

        <button className="carousel-btn prev" onClick={prevSlide}>
          <ChevronLeft size={28} />
        </button>
        <button className="carousel-btn next" onClick={nextSlide}>
          <ChevronRight size={28} />
        </button>

        <div className="carousel-dots">
          {heroImages.map((_, index) => (
            <button
              key={index}
              className={`dot ${index === currentSlide ? 'active' : ''}`}
              onClick={() => setCurrentSlide(index)}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
